import type { PlayerErrorCode, PlayerState, StartPhase } from "./playerState";

export interface PlayerPresentation {
  title: string;
  hint?: string;
  tone: "neutral" | "danger";
  showSpinner: boolean;
  canRetry: boolean;
}

export interface PlayerContext {
  /** A slot request for this camera is already outstanding, waiting for
   *  someone who manages cameras to answer it. Changes how quota_exhausted
   *  reads — see describePlayerState. */
  isSlotRequested?: boolean;
}

const PHASE_COPY: Record<StartPhase, string> = {
  requesting: "Requesting stream…",
  connecting: "Connecting to camera…",
  buffering_segments: "Buffering…",
  attaching_player: "Starting playback…",
};

// canRetry is false for error classes where retrying changes nothing without
// an operator acting first — a disabled camera stays disabled, a full grid
// stays full — so a retry button there would just be a broken promise.
const ERROR_COPY: Record<PlayerErrorCode, { title: string; hint: string; canRetry: boolean }> = {
  start_failed: { title: "Couldn't start stream", hint: "The server rejected the request.", canRetry: true },
  camera_disabled: { title: "Camera is disabled", hint: "Enable it from the camera list to view.", canRetry: false },
  timeout: { title: "Timed out", hint: "The camera didn't respond in time.", canRetry: true },
  stopped_by_server: { title: "Stream stopped", hint: "The server ended this stream.", canRetry: true },
  lease_lost: { title: "Connection lost", hint: "Your viewing session expired.", canRetry: true },
  hls_network: { title: "Network error", hint: "Lost connection to the video segments.", canRetry: true },
  hls_media: { title: "Decode error", hint: "The video stream could not be decoded.", canRetry: true },
  hls_other: { title: "Playback error", hint: "Something went wrong.", canRetry: true },
  unsupported_browser: { title: "Unsupported browser", hint: "This browser cannot play HLS video.", canRetry: false },
  // canRetry false: pressing play again changes nothing while the wall is
  // full. The tile offers to ask for a slot instead (see describePlayerState
  // and StreamContentionDialog), which is a different act — re-asking the
  // server, not retrying a failed request — and for a viewer who can't
  // evict, a different permission entirely.
  quota_exhausted: {
    title: "No free stream slots",
    hint: "Every allowed stream is already running.",
    canRetry: false,
  },
  // canRetry false: the segments are arriving fine and will keep arriving in a
  // codec this device has no decoder for. Retrying re-downloads them to fail
  // the same way, which is what the generic media-error path did before this
  // code existed — several attempts, a growing delay, then "Repeated failures",
  // none of which hinted that the problem was the video format.
  unsupported_codec: {
    title: "Can't play this camera here",
    hint: "This camera streams H.265, which this browser or device can't decode. Try Safari, or a device with H.265 support.",
    canRetry: false,
  },
};

export function describePlayerState(state: PlayerState, context: PlayerContext = {}): PlayerPresentation {
  switch (state.kind) {
    case "idle":
      return { title: "Not watching", tone: "neutral", showSpinner: false, canRetry: false };
    // Neutral, with a spinner: this tile is going to start on its own as soon
    // as there is room, so it reads as pending rather than broken. No retry
    // button — pressing one would only race the automatic re-attempt that is
    // already scheduled.
    case "queued":
      return {
        title: "Waiting for a free slot…",
        hint: "This tile starts automatically when another stops.",
        tone: "neutral",
        showSpinner: true,
        canRetry: false,
      };
    case "starting":
      return { title: PHASE_COPY[state.phase], tone: "neutral", showSpinner: true, canRetry: false };
    case "playing":
      return { title: "Live", tone: "neutral", showSpinner: false, canRetry: false };
    case "buffering":
      return { title: "Buffering…", tone: "neutral", showSpinner: true, canRetry: false };
    case "paused":
      return { title: "Paused", tone: "neutral", showSpinner: false, canRetry: false };
    // Neutral, not danger: hls.js's own fast recovery gave up, but the
    // backend's RTSP pull keeps retrying forever (see useHlsStream's own
    // comment on this), so this browser keeps trying alongside it rather
    // than dead-ending. canRetry stays true — an operator who wants it now
    // rather than waiting out the backoff shouldn't have to.
    case "reconnecting":
      return {
        title: `Reconnecting… (attempt ${state.attempt})`,
        hint: "Automatic recovery is still trying — this can take a while.",
        tone: "neutral",
        showSpinner: true,
        canRetry: true,
      };
    case "error": {
      // Not a fault and not a dead end: somebody who can free a slot has
      // already been asked, the answer arrives over the notice channel, and
      // the page starts this tile itself if it's approved (see
      // LiveGridPage's notice subscription). Same reasoning as `queued` —
      // waiting is the ordinary consequence of a full wall, not something to
      // paint red. Without this, the tile showed a red "No free stream
      // slots" while the dialog two inches away said "Request sent": two
      // stories about the one fact.
      if (state.code === "quota_exhausted" && context.isSlotRequested) {
        return {
          title: "Waiting for approval…",
          hint: "Somebody who manages cameras has been asked to free a slot.",
          tone: "neutral",
          showSpinner: true,
          canRetry: false,
        };
      }
      const copy = ERROR_COPY[state.code];
      return {
        title: copy.title,
        hint: state.detail ?? copy.hint,
        tone: "danger",
        showSpinner: false,
        canRetry: copy.canRetry,
      };
    }
  }
}
